<template>
  <div class="w-full">
    <section
      class="relative overflow-hidden bg-gradient-to-br from-[#eff6ff] via-[#f0f9ff] to-[#f8fafc]"
    >
      <div class="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:py-24">
        <div class="max-w-3xl">
          <p
            class="text-xs font-bold uppercase tracking-[0.25em] text-[#3b82f6] mb-3"
          >
            About myDid
          </p>
          <h1 class="text-4xl sm:text-5xl font-extrabold tracking-tight">
            Build communities that reward real participation.
          </h1>
          <p class="mt-5 text-base sm:text-lg text-[#111814]/70">
            Issue verifiable badges, unlock levels, and recognize contributions
            with credentials that stay under the user control.
          </p>
          <div class="mt-8 flex flex-col sm:flex-row gap-3">
            <router-link
              class="rounded-full bg-[#111814] px-6 py-3 text-sm font-bold text-white hover:opacity-90 text-center"
              to="/badge-customizer"
            >
              Explore badges
            </router-link>
            <a
              href="https://swagger.mydid.com"
              target="_blank"
              rel="noopener noreferrer"
              class="rounded-full border border-[#111814]/15 px-6 py-3 text-sm font-bold text-[#111814] hover:border-[#3b82f6]/40 text-center"
            >
              View API documentation
            </a>
          </div>
        </div>
      </div>
      <div
        class="absolute -right-24 top-10 h-56 w-56 rounded-full bg-[#3b82f6]/10 blur-3xl"
      ></div>
      <div
        class="absolute -left-24 bottom-10 h-64 w-64 rounded-full bg-[#3b82f6]/10 blur-3xl"
      ></div>
    </section>

    <section class="bg-white">
      <div class="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <div class="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div>
            <p
              class="text-xs font-bold uppercase tracking-[0.25em] text-[#3b82f6]"
            >
              Why myDid?
            </p>
            <h2 class="mt-3 text-3xl font-bold">
              A new way to animate your community
            </h2>
          </div>
          <div class="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div class="rounded-2xl border border-black/5 bg-blue-50/50 p-6">
              <h3 class="text-xl font-bold mb-2">Badges</h3>
              <p class="text-sm text-[#111814]/60">
                Issue immutable credentials that certify skills and membership.
              </p>
            </div>
            <div class="rounded-2xl border border-black/5 bg-blue-50/50 p-6">
              <h3 class="text-xl font-bold mb-2">Points</h3>
              <p class="text-sm text-[#111814]/60">
                Measure engagement with transparent on-chain points.
              </p>
            </div>
            <div class="rounded-2xl border border-black/5 bg-blue-50/50 p-6">
              <h3 class="text-xl font-bold mb-2">Levels</h3>
              <p class="text-sm text-[#111814]/60">
                Unlock access and privileges based on reputation.
              </p>
            </div>
            <div class="rounded-2xl border border-black/5 bg-blue-50/50 p-6">
              <h3 class="text-xl font-bold mb-2">Rewards</h3>
              <p class="text-sm text-[#111814]/60">
                Automate rewards for your most active members.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="bg-[#eff6ff]/50">
      <div class="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <div class="text-center mb-10">
          <h2 class="text-3xl font-bold">How it works</h2>
          <p class="mt-2 text-sm text-[#111814]/60">
            Robust infrastructure, simple workflows.
          </p>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div class="rounded-2xl border border-black/5 bg-white p-6">
            <div class="text-xs font-bold text-[#3b82f6] mb-3">STEP 01</div>
            <h4 class="text-lg font-bold mb-2">Create</h4>
            <p class="text-sm text-[#111814]/60">
              Build credential templates with the UI or API.
            </p>
          </div>
          <div class="rounded-2xl border border-black/5 bg-white p-6">
            <div class="text-xs font-bold text-[#3b82f6] mb-3">STEP 02</div>
            <h4 class="text-lg font-bold mb-2">Define</h4>
            <p class="text-sm text-[#111814]/60">
              Set criteria, quests, votes, or time-based rules.
            </p>
          </div>
          <div class="rounded-2xl border border-black/5 bg-white p-6">
            <div class="text-xs font-bold text-[#3b82f6] mb-3">STEP 03</div>
            <h4 class="text-lg font-bold mb-2">Deliver</h4>
            <p class="text-sm text-[#111814]/60">
              Send badges to user wallets instantly.
            </p>
          </div>
          <div class="rounded-2xl border border-black/5 bg-white p-6">
            <div class="text-xs font-bold text-[#3b82f6] mb-3">STEP 04</div>
            <h4 class="text-lg font-bold mb-2">Verify</h4>
            <p class="text-sm text-[#111814]/60">
              Validate authenticity without centralized databases.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section class="bg-white">
      <div class="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <div class="text-center mb-10">
          <h2 class="text-3xl font-bold">Our values</h2>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div
            class="rounded-2xl border border-black/5 bg-blue-50/50 p-6 text-center"
          >
            <h4 class="text-lg font-bold mb-2">Ownership</h4>
            <p class="text-sm text-[#111814]/60">
              Users own their data and credentials.
            </p>
          </div>
          <div
            class="rounded-2xl border border-black/5 bg-blue-50/50 p-6 text-center"
          >
            <h4 class="text-lg font-bold mb-2">Privacy by design</h4>
            <p class="text-sm text-[#111814]/60">
              Privacy is the foundation, not an option.
            </p>
          </div>
          <div
            class="rounded-2xl border border-black/5 bg-blue-50/50 p-6 text-center"
          >
            <h4 class="text-lg font-bold mb-2">Trust & Proof</h4>
            <p class="text-sm text-[#111814]/60">
              Replace blind trust with verification.
            </p>
          </div>
          <div
            class="rounded-2xl border border-black/5 bg-blue-50/50 p-6 text-center"
          >
            <h4 class="text-lg font-bold mb-2">Community first</h4>
            <p class="text-sm text-[#111814]/60">
              Built for and with open communities.
            </p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'About',
};
</script>
